import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';

import { Documents } from '@amzn/coral_com-amazon-musicpaperrecordkeeperservice';

import { ColDef, GridApi } from 'ag-grid-community';
import { isNil } from 'lodash';
import { useHistory } from 'react-router';
import { IndexMetadata } from 'src/components/Agreements/Agreements';
import {
    FileNameRenderer,
    LicensorsRenderer,
    LinksRenderer,
    SearchHitsRenderer,
    ServicesRenderer
} from 'src/components/Agreements/AgreementsTable/cell-renderers';
import { createDocumentRows, createSearchIndexEntries } from 'src/components/Agreements/AgreementsTable/util-functions';
import { SearchContext } from 'src/components/Agreements/SearchContext';
import { listAllDocuments } from 'src/components/Agreements/Shared';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { AppContext } from 'src/contexts/appContext';
import { useAsyncEffect } from 'src/util/async';
import { SearchIndex } from 'src/util/search-index';

import { DocumentRow } from './grid-types';

interface IAgreementsTable {
    loading: boolean;
    setLoading: Dispatch<SetStateAction<boolean>>;
    setErrorMessage: Dispatch<SetStateAction<string | undefined>>;
    licensors?: string[] | undefined;
    uploadCounter?: number;
}

export const AgreementsTable: React.FC<IAgreementsTable> = (props) => {
    const { setLoading, setErrorMessage, loading } = props;
    const { entities, userInfo } = useContext(AppContext);
    const { tableFilters, searchResults, setSearchIndex } = useContext(SearchContext);
    const history = useHistory();
    const [documents, setDocuments] = useState<Documents>([]);
    const [gridApi, setGridApi] = useState<GridApi | undefined>();
    const licensors: Set<string> | undefined = useMemo(() => {
        return isNil(props.licensors) ? undefined : new Set(props.licensors);
    }, [props.licensors]);

    useEffect(() => {
        setSearchIndex(new SearchIndex<IndexMetadata>(createSearchIndexEntries(documents)));
    }, [documents]);

    const gridData: DocumentRow[] = useMemo(() => {
        return createDocumentRows(documents, licensors, searchResults);
    }, [documents, licensors, searchResults]);

    useEffect(() => {
        if (!isNil(gridApi)) {
            gridApi.forEachNode((node) => node.setExpanded(!isNil(searchResults)));
        }
    }, [gridApi, gridData, searchResults]);

    useAsyncEffect(async () => {
        setLoading(true);
        await listAllDocuments(userInfo.username, setErrorMessage)
            .then(setDocuments)
            .finally(() => setLoading(false));
    }, [props.uploadCounter]);

    const columnDefs: ColDef[] = useMemo(
        () => [
            {
                headerName: 'Licensors',
                field: 'licensors',
                cellRenderer: LicensorsRenderer()
            },
            {
                headerName: 'File Name',
                field: 'resolvedName',
                minWidth: 300,
                filter: 'agTextColumnFilter',
                cellRenderer: FileNameRenderer(setLoading, setErrorMessage)
            },
            {
                headerName: 'Original File Name',
                field: 'name',
                minWidth: 300,
                filter: 'agTextColumnFilter',
                cellRenderer: FileNameRenderer(setLoading, setErrorMessage),
                initialHide: true
            },
            {
                headerName: 'Services',
                field: 'services',
                cellRenderer: ServicesRenderer()
            },
            {
                headerName: 'Start Date',
                field: 'startDate'
            },
            {
                headerName: 'End Date',
                field: 'endDate'
            },
            {
                headerName: 'Links',
                field: 'links',
                cellRenderer: LinksRenderer()
            },
            {
                headerName: 'Date Added',
                field: 'uploadedAt',
                cellDataType: 'dateTime'
            },
            {
                headerName: 'Added By',
                field: 'uploadedBy',
                initialHide: true
            },
            {
                headerName: 'Document ID',
                field: 'id',
                initialHide: true
            }
        ],
        [history, entities, setErrorMessage, setLoading]
    );

    useEffect(() => {
        if (!isNil(gridApi)) {
            const newFilterModel = { ...gridApi.getFilterModel() };
            Object.keys(tableFilters).forEach((headerName) => {
                const colDef = columnDefs.find((def) => def.headerName === headerName);

                if (!isNil(colDef)) {
                    if (colDef.filter === 'agTextColumnFilter') {
                        newFilterModel[colDef.field!] = {
                            filterType: 'text',
                            type: 'equals',
                            filter: tableFilters[headerName]?.[0]
                        };
                    } else {
                        newFilterModel[colDef.field!] = {
                            filterType: 'set',
                            values: tableFilters[headerName]
                        };
                    }
                }
            });
            gridApi.setFilterModel(newFilterModel);
        }
    }, [tableFilters, gridApi, columnDefs]);

    return (
        <BaseAgGrid
            gridOptions={{
                autoSizeStrategy: {
                    type: 'fitGridWidth'
                }
            }}
            columnDefs={columnDefs}
            rowData={gridData}
            suppressRowVirtualisation={true}
            suppressColumnVirtualisation={true}
            masterDetail={true}
            isRowMaster={() => true}
            detailCellRenderer={SearchHitsRenderer}
            setAgGridApi={setGridApi}
            detailRowAutoHeight={true}
            loading={loading}
        />
    );
};
